/* eslint-disable import/no-cycle */
import angular, { IHttpService } from 'angular';
import { PermissionsService } from '../../components/authorization/permissions.service';
import {
  EFileSection,
  EStaticFileType,
  File as AutoboardFile,
  FileUploadRequest,
  StaticFile,
  StaticOptions,
  StaticUploadRequest,
} from '../../src/app/@shared-types/misc/file';
import { ToastService } from '../../ui-components/toast/toast.service';
import { FileAPIService } from '../api/file-api.service';
import { StringService } from '../misc/string.service';

export type FileServiceUploadRequest = Pick<FileUploadRequest, 'identifier' | 'section' | 'type'>;

/* @ngInject */
export class FileService {
  private toastService: ToastService;

  private permissionsService: PermissionsService;

  private stringService: StringService;

  private fileApiService: FileAPIService;

  private $http: IHttpService;

  constructor(
    toastService: ToastService,
    permissionsService: PermissionsService,
    stringService: StringService,
    fileApiService: FileAPIService,
    $http: IHttpService,
  ) {
    'ngInject';

    this.toastService = toastService;
    this.permissionsService = permissionsService;
    this.stringService = stringService;
    this.fileApiService = fileApiService;
    this.$http = $http;
  }

  public deleteFile = (fileId: string): Promise<void> => this.fileApiService.deleteFile(fileId);

  public deleteStatic = (staticId: string): Promise<void> => this
    .fileApiService.deleteStatic(staticId);

  public getFileMeta = (fileId: string): Promise<AutoboardFile> => this
    .fileApiService.getMeta(fileId);

  public getByContext = (
    section: EFileSection,
    identifier: string,
  ): Promise<AutoboardFile[]> => this.fileApiService.getByContext(section, identifier);

  public getFile = (
    fileId: string,
    fileName: string,
  ): Promise<void> => new Promise((resolve, reject) => {
    this.$http
      .get(`${API_URL}/api/files/file/${fileId}`, {
        responseType: 'arraybuffer',
      })
      .then((response) => {
        const a = document.createElement('a');
        document.body.appendChild(a);
        const file = new Blob([response.data as string], {});
        const fileURL = window.URL.createObjectURL(file);
        a.href = fileURL;
        a.download = fileName;
        a.click();
        resolve();
      })
      .catch((e) => reject(e));
  });

  public uploadContextualFile = (
    file: File,
    progressBarId: string,
    fileRequest: FileServiceUploadRequest,
  ): Promise<number> => new Promise((resolve, reject) => {
    const fileData = new FormData();
    fileData.append('file', file);
    fileData.append('type', fileRequest.type);
    fileData.append('identifier', fileRequest.identifier);
    fileData.append('section', fileRequest.section);
    fileData.append('extension', this.stringService.getExtensionFromFileName(file.name) || 'na');

    const progressBar = progressBarId !== undefined ? document.getElementById(progressBarId) : null;
    if (progressBar) progressBar.style.opacity = '1';

    this.$http({
      method: 'POST',
      url: `${API_URL}/api/files/file`,
      data: fileData,
      transformRequest: angular.identity,
      headers: { 'Content-Type': undefined },
      uploadEventHandlers: {
        progress(e) {
          if (progressBar !== null) {
            if ((e as ProgressEvent).lengthComputable) {
              progressBar.setAttribute('value', String((e as ProgressEvent).loaded));
              progressBar.setAttribute('max', String((e as ProgressEvent).total));
            }
          }
        },
      },
    })
      .then((res) => {
        const fileId: number = res.data as number;
        return resolve(fileId);
      })
      .catch((e) => reject(e));
  });

  public getStaticsByIdentifierAndType = (
    type: EStaticFileType,
    identifier: string,
  ): Promise<StaticFile[]> => this.fileApiService.staticsByIdentifierAndType(identifier, type);

  public apiUploadStatic = (
    file: File,
    staticUploadRequest: StaticUploadRequest,
    progressBarId?: string,
  ): Promise<string> => new Promise((resolve, reject) => {
    const fileData = new FormData();
    fileData.append('file', file);
    fileData.append('type', staticUploadRequest.type);
    fileData.append('identifier', staticUploadRequest.identifier);

    // eslint-disable-next-line max-len
    const progressBar = progressBarId !== undefined ? document.getElementById(progressBarId) : null;
    if (progressBar) progressBar.style.opacity = '1';

    this.$http({
      method: 'POST',
      url: `${API_URL}/static/f/upload`,
      data: fileData,
      transformRequest: angular.identity,
      headers: {
        'Content-Type': undefined,
      },
      uploadEventHandlers: {
        progress(e) {
          if (progressBar) {
            if ((e as ProgressEvent).lengthComputable) {
              const { total, loaded } = (e as ProgressEvent);
              const percentage = loaded / total;

              progressBar.style.width = `${percentage * 100}%`;
              progressBar.setAttribute('value', String((e as ProgressEvent).loaded));
              progressBar.setAttribute('max', String((e as ProgressEvent).total));
            }
          }
        },
      },
    })
      .then((res) => resolve(res.data as string))
      .catch((e) => reject(e));
  });

  public getStaticUrl = (
    staticId: string,
    options?: StaticOptions,
  ): string => {
    const params = options ? `?${new URLSearchParams(options as Record<string, string>).toString()}` : '';
    return `${API_URL}/static/f/view/${staticId}${params}`;
  };

  public getStaticPreviewUrl = (
    staticId: string,
  ): string => `${API_URL}/static/f/preview/${staticId}`;
}

export default angular
  .module('services.files', [])
  .service('fileService', FileService).name;
